import { useActiveBlocksuiteEditor } from '@affine/core/components/hooks/use-block-suite-editor';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { Bound } from '@blocksuite/affine/global/gfx';
import { DocModeProvider } from '@blocksuite/affine/shared/services';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef } from 'react';

import { parseBranchHref } from '../handlers/branch-link-format';
import { parseNotesHref } from '../handlers/notes-link-format';
import {
  flashBlock,
  parseCardHref,
  parseSourceHref,
} from '../handlers/source-link-format';

/**
 * Canvas-link Router
 *
 * Mounted once at workspace level. Listens (capture-phase) for clicks
 * on any `<a>` element whose `href` carries one of our custom URL
 * schemes, then routes the navigation:
 *
 *  - `curiescious-card://<noteId>`   → switch to edgeless + pan the
 *    surface viewport to the card so the user sees the synthesised
 *    capture next to whatever else is on the canvas.
 *
 *  - `curiescious-source://<docId>/<blockId>` → switch to page mode +
 *    scroll the source block into view + flash-highlight it for ~2s
 *    so the user can locate the original passage at a glance.
 *
 * We intercept at `document.body` (not inside BlockSuite's AffineLink)
 * so the integration is one self-contained component — easy to revert
 * and zero touchpoints inside the BlockSuite editor's link plumbing.
 */
export function CanvasLinkRouter() {
  const [activeEditor] = useActiveBlocksuiteEditor();
  const editorRef = useRef(activeEditor);
  useEffect(() => {
    editorRef.current = activeEditor;
  }, [activeEditor]);

  const workbenchService = useService(WorkbenchService);
  const openBranchInPane = useCallback(
    (branchDocId: string) => {
      try {
        workbenchService.workbench.openDoc(branchDocId, { at: 'beside' });
      } catch {
        try {
          workbenchService.workbench.openDoc(branchDocId);
        } catch {
          /* nothing else to fall back to */
        }
      }
    },
    [workbenchService]
  );

  const openNotesEntry = useCallback(
    (notesDocId: string, entryId: string) => {
      // Structured form gets serialized to `?blockIds=<entryId>` via
      // the workbench's URL params helper, and AffineOS's existing
      // scroll-to-block plumbing then takes us to the entry's
      // divider. `mode: 'page'` keeps us out of edgeless — notes
      // are a reading view, not a canvas.
      try {
        workbenchService.workbench.openDoc({
          docId: notesDocId,
          mode: 'page',
          blockIds: [entryId],
        });
      } catch {
        try {
          workbenchService.workbench.openDoc(notesDocId);
        } catch {
          /* nothing else to fall back to */
        }
      }
    },
    [workbenchService]
  );

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      if (!href) return;

      const cardId = parseCardHref(href);
      if (cardId) {
        e.preventDefault();
        e.stopPropagation();
        jumpToCard(editorRef.current as unknown as EditorLike, cardId).catch(
          err => {
            console.warn('[canvas-link-router] jumpToCard failed:', err);
          }
        );
        return;
      }

      const source = parseSourceHref(href);
      if (source) {
        e.preventDefault();
        e.stopPropagation();
        jumpToSource(
          editorRef.current as unknown as EditorLike,
          source.blockId
        ).catch(err => {
          console.warn('[canvas-link-router] jumpToSource failed:', err);
        });
        return;
      }

      const branchDocId = parseBranchHref(href);
      if (branchDocId) {
        e.preventDefault();
        e.stopPropagation();
        openBranchInPane(branchDocId);
        return;
      }

      const notes = parseNotesHref(href);
      if (notes) {
        e.preventDefault();
        e.stopPropagation();
        openNotesEntry(notes.notesDocId, notes.entryId);
        return;
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [openBranchInPane, openNotesEntry]);

  return null;
}

type EditorLike = {
  host?: {
    std: {
      get: (key: unknown) => unknown;
      view: { getBlock: (id: string) => unknown };
      store: { getBlock: (id: string) => unknown };
    };
  };
} | null;

async function jumpToCard(
  editor: EditorLike,
  cardNoteId: string
): Promise<void> {
  const host = editor?.host;
  if (!host) return;
  // Switch to edgeless first so the surface viewport is mounted by
  // the time we ask it to pan.
  try {
    (host.std.get(DocModeProvider) as { setEditorMode?: (m: string) => void })
      .setEditorMode?.('edgeless');
  } catch {
    /* mode provider unavailable */
  }
  // Wait a paint so the edgeless renderer mounts.
  await new Promise<void>(r => setTimeout(r, 120));
  try {
    const note = (host.std.store.getBlock(cardNoteId) as
      | { model?: { props?: { xywh?: string } } }
      | undefined)?.model;
    const xywh = note?.props?.xywh;
    if (!xywh) return;
    const bound = Bound.deserialize(xywh);
    // Pan + zoom with a 80px padding so the card isn't flush against
    // the viewport edges.
    const gfx = (host.std as unknown as {
      gfx?: {
        viewport?: {
          setViewportByBound?: (
            b: Bound,
            padding?: [number, number, number, number],
            smooth?: boolean
          ) => void;
        };
      };
    }).gfx;
    gfx?.viewport?.setViewportByBound?.(bound, [80, 80, 80, 80], true);
  } catch {
    /* viewport unavailable — user is at least in edgeless now */
  }
}

async function jumpToSource(
  editor: EditorLike,
  blockId: string
): Promise<void> {
  const host = editor?.host;
  if (!host) return;
  // Flip to page mode so the block actually renders as scrollable doc
  // text (in edgeless it'd be inside a note we'd have to pan to).
  try {
    (host.std.get(DocModeProvider) as { setEditorMode?: (m: string) => void })
      .setEditorMode?.('page');
  } catch {
    /* mode provider unavailable */
  }
  await new Promise<void>(r => setTimeout(r, 120));
  try {
    const blockView = host.std.view.getBlock(blockId) as
      | { scrollIntoView?: (opts: ScrollIntoViewOptions) => void }
      | undefined;
    blockView?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  } catch {
    /* block not mounted yet — user is at least in page mode */
  }
  // Flash-highlight via a 2s yellow background on the block's text.
  try {
    const store = (host.std as unknown as { store?: unknown }).store as
      | Parameters<typeof flashBlock>[0]
      | undefined;
    if (store) flashBlock(store, blockId);
  } catch {
    /* ignore */
  }
}
